import React, { useState, useEffect } from 'react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

// ==========================================
// CONFIGURATION
// Updated launch date and Ganesh Chaturthi theme details
// ==========================================
const LAUNCH_CONFIG = {
  targetDate: '2026-09-14T12:00:00+05:30', // 12:00 PM IST on September 14 (Ganesh Chaturthi)
  title: 'NEXGENT',
  subtitle: 'PRIVATE LEADERSHIP PLATFORM',
  statusBadge: 'GANESH CHATURTHI SPECIAL PRE-LAUNCH',
};

interface TimeLeft {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
}

export const CyberCountdownGanesh: React.FC = () => {
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [isLaunched, setIsLaunched] = useState(false);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const difference = +new Date(LAUNCH_CONFIG.targetDate) - +new Date();

      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
          minutes: Math.floor((difference / 1000 / 60) % 60),
          seconds: Math.floor((difference / 1000) % 60),
        });
        setIsLaunched(false);
      } else {
        setIsLaunched(true);
      }
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, []);

  const timeUnits = [
    { label: 'DAYS', value: timeLeft.days },
    { label: 'HOURS', value: timeLeft.hours },
    { label: 'MINUTES', value: timeLeft.minutes },
    { label: 'SECONDS', value: timeLeft.seconds },
  ];

  return (
    <>
      <div className="bg-slate-950 min-h-screen text-slate-100 flex flex-col justify-between">
        <LandingHeader />

        <div className="flex-grow flex items-center justify-center p-6 font-mono">
          {/* Main Container Card with Festive Golden/Amber Border */}
          <div className="relative w-full max-w-4xl bg-slate-900/90 backdrop-blur-xl border border-amber-500/20 rounded-2xl p-8 sm:p-12 shadow-2xl shadow-amber-950/30 overflow-hidden">
            
            {/* Auspicious Golden Ambient Lights */}
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-amber-500/15 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-orange-600/15 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-yellow-500/5 rounded-full blur-3xl pointer-events-none" />

            {/* Corner Decorative Accents */}
            <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-amber-500/80 rounded-tl-2xl" />
            <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 border-amber-500/80 rounded-tr-2xl" />
            <div className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 border-orange-500/80 rounded-bl-2xl" />
            <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-orange-500/80 rounded-br-2xl" />

            {/* Header Section */}
            <div className="flex flex-col items-center text-center mb-10 relative z-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-950/40 border border-amber-500/30 mb-5 backdrop-blur-md">
                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse shadow-sm shadow-amber-400" />
                <span className="text-xs font-semibold tracking-widest text-amber-300 uppercase">
                  {LAUNCH_CONFIG.statusBadge}
                </span>
              </div>

              <h1 className="text-4xl sm:text-6xl font-black tracking-wider bg-gradient-to-r from-amber-200 via-amber-400 to-orange-500 bg-clip-text text-transparent mb-3 drop-shadow-sm">
                {LAUNCH_CONFIG.title}
              </h1>

              {/* Subtitle with Golden Gradient */}
              <p className="text-xs sm:text-sm tracking-[0.3em] text-slate-400 uppercase">
                {LAUNCH_CONFIG.subtitle} —{' '}
                <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent font-bold">
                  SEPTEMBER 14
                </span>
              </p>
            </div>

            {/* Info Section */}
            <div className="mb-10 text-center max-w-3xl mx-auto relative z-10">
              <p className="text-base sm:text-lg text-slate-200 leading-relaxed font-sans">
                A private leadership hiring network connecting{' '}
                <span className="text-amber-400 font-semibold underline decoration-amber-500/40 underline-offset-4">
                  verified recruiters
                </span>{' '}
                with{' '}
                <span className="text-orange-400 font-semibold underline decoration-orange-500/40 underline-offset-4">
                  experienced professionals
                </span>.
              </p>

              <div className="flex flex-wrap justify-center gap-2.5 mt-6 font-sans">
                <span className="px-3.5 py-1.5 border border-amber-500/20 bg-amber-950/20 rounded-full text-xs text-amber-200">
                  Verified Profiles
                </span>
                <span className="px-3.5 py-1.5 border border-amber-500/40 bg-amber-500/10 rounded-full text-xs text-amber-300 font-medium">
                  Auspicious Pre-Launch
                </span>
                <span className="px-3.5 py-1.5 border border-orange-500/30 bg-orange-500/10 rounded-full text-xs text-orange-300 font-medium">
                  Leadership Opportunities
                </span>
                <span className="px-3.5 py-1.5 border border-amber-500/20 bg-amber-950/20 rounded-full text-xs text-amber-200">
                  Curated Talent Network
                </span>
              </div>
            </div>

            {/* Countdown Grid */}
            {isLaunched ? (
              <div className="text-center py-8 relative z-10">
                <h2 className="text-3xl sm:text-5xl font-extrabold bg-gradient-to-r from-amber-300 via-amber-400 to-orange-500 bg-clip-text text-transparent tracking-widest animate-bounce">
                  SYSTEM IS LIVE
                </h2>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 relative z-10">
                {timeUnits.map((unit, index) => (
                  <div
                    key={index}
                    className="relative group bg-slate-950/70 border border-slate-800 hover:border-amber-500/60 rounded-xl p-4 sm:p-6 text-center transition-all duration-300 shadow-lg hover:shadow-amber-500/10"
                  >
                    {/* Numeric Display */}
                    <div className="text-4xl sm:text-6xl font-black text-slate-100 group-hover:text-amber-400 transition-colors duration-300 tracking-tight">
                      {String(unit.value).padStart(2, '0')}
                    </div>

                    {/* Saffron/Gold Accent Line */}
                    <div className="w-8 h-[2px] bg-slate-800 group-hover:bg-gradient-to-r group-hover:from-amber-400 group-hover:to-orange-500 mx-auto my-3 transition-all duration-300" />

                    {/* Unit Label */}
                    <span className="text-[10px] sm:text-xs font-bold text-amber-500/80 tracking-widest uppercase">
                      {unit.label}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Footer Status Bar */}
            <div className="mt-10 pt-6 border-t border-slate-800/80 flex flex-col sm:flex-row items-center justify-between text-xs text-slate-400 gap-2 relative z-10 font-sans">
              <span className="font-mono">TARGET: SEPTEMBER 14, 2026 • 12:00 PM IST</span>
              <span className="text-amber-400 font-semibold flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-ping" />
                PRE-LAUNCH READY
              </span>
            </div>

          </div>
        </div>

        <LandingFooter />
      </div>
    </>
  );
};

export default CyberCountdownGanesh;